import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import i18n from '../i18n';
import { rememberHospitalBranding } from '../utils/hospitalBranding';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    rememberHospitalBranding(user);
  }, [user]);

  const checkAuthStatus = async () => {
    try {
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      if (
        path.startsWith('/doctor') ||
        path.startsWith('/admin') ||
        path.startsWith('/front-desk')
      ) {
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        // Check if there's a refresh token for persistent sessions
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          // Try to refresh the access token
          try {
            const response = await axios.post('/api/auth/refresh', { refreshToken });
            const newToken = response.data.token;
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            setToken(newToken);
            
            // Get user data with new token
            const userResponse = await axios.get('/api/auth/me');
            const userData = userResponse.data.user;
            setUser(userData);
            setIsAuthenticated(true);
            const role = userData?.role || 'patient';
            const userId = userData?.patientId || userData?.doctorId || userData?._id || userData?.id;
            if (userId && typeof window !== 'undefined') {
              const savedLang = localStorage.getItem(`language_${role}_${userId}`);
              if (savedLang) i18n.changeLanguage(savedLang);
            }
          } catch (refreshError) {
            // Refresh token is invalid or expired
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
            setToken(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setToken(null);
          setIsAuthenticated(false);
        }
        setLoading(false);
        return;
      }

      // Set token in axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setToken(token);
      
      // Verify token with backend
      const response = await axios.get('/api/auth/me');
      const userData = response.data.user;
      setUser(userData);
      setIsAuthenticated(true);

      // Apply per-user language preference on app load
      const role = userData.role || 'patient';
      const userId = userData.patientId || userData.doctorId || userData._id || userData.id;
      if (userId && typeof window !== 'undefined') {
        const savedLang = localStorage.getItem(`language_${role}_${userId}`);
        if (savedLang) i18n.changeLanguage(savedLang);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      
      // If access token expired and we have refresh token, try to refresh
      if (error.response?.status === 401) {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          try {
            const refreshResponse = await axios.post('/api/auth/refresh', { refreshToken });
            const newToken = refreshResponse.data.token;
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            setToken(newToken);
            
            // Get user data with new token
            const userResponse = await axios.get('/api/auth/me');
            const userData = userResponse.data.user;
            setUser(userData);
            setIsAuthenticated(true);
            const role = userData?.role || 'patient';
            const userId = userData?.patientId || userData?.doctorId || userData?._id || userData?.id;
            if (userId && typeof window !== 'undefined') {
              const savedLang = localStorage.getItem(`language_${role}_${userId}`);
              if (savedLang) i18n.changeLanguage(savedLang);
            }
            setLoading(false);
            return;
          } catch (refreshError) {
            // Both tokens are invalid
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            setToken(null);
          }
        }
      }
      
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (identifier, password, rememberMe = false) => {
    try {
      setLoading(true);
      console.log('Sending login request:', { identifier, hasPassword: !!password, rememberMe });
      const response = await axios.post('/api/auth/login', {
        identifier,
        password,
        rememberMe
      });

      const { token, refreshToken, user, sessionId } = response.data;
      
      // Store tokens
      localStorage.setItem('token', token);
      
      // Store session ID for logout tracking
      if (sessionId) {
        localStorage.setItem('sessionId', sessionId);
      }
      
      // If remember me is checked, store refresh token for persistent sessions
      if (rememberMe && refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setToken(token);
      
      // Set user state
      setUser(user);
      setIsAuthenticated(true);
      axios.get('/api/auth/me')
        .then((meResponse) => {
          if (meResponse.data?.user) setUser((current) => ({ ...current, ...meResponse.data.user }));
        })
        .catch(() => {});

      // Apply per-user language preference so languages don't leak between accounts
      const role = user.role || 'patient';
      const userId = user.patientId || user.doctorId || user._id || user.id;
      if (userId && typeof window !== 'undefined') {
        const savedLang = localStorage.getItem(`language_${role}_${userId}`);
        if (savedLang) i18n.changeLanguage(savedLang);
      }

      toast.success(`Welcome back, ${user.firstName}!`);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      const data = error.response?.data;
      const message =
        (data && typeof data.message === 'string' && data.message) ||
        (data?.errors?.[0]?.msg) ||
        (error.response?.status === 401 ? 'Invalid Patient ID or password. Register at the hospital front desk to receive a patient ID for that hospital.' : 'Login failed');
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await axios.post('/api/auth/register', userData);

      const { patientId, user } = response.data;
      
      // DO NOT store tokens or log user in
      // DO NOT set authentication state
      // Just show success message
      
      // Show success message with Patient ID
      toast.success(
        `Registration successful! Your Patient ID is: ${patientId}. Please login to continue.`,
        { duration: 2000 }
      );
      
      return { success: true, patientId };
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(async () => {
    try {
      const sessionId = localStorage.getItem('sessionId');
      if (sessionId) {
        // Call logout endpoint to track session end
        await axios.post('/api/auth/logout', { sessionId }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }).catch(err => {
          // Don't fail logout if API call fails
          console.error('Logout API error:', err);
        });
      }
    } catch (error) {
      // Don't fail logout if API call fails
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionId');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      toast.success('Logged out successfully');
    }
  }, []);

  // Auto-logout after 10 minutes of inactivity (only for patients)
  useEffect(() => {
    if (!isAuthenticated || !user || user.role !== 'patient') {
      return; // Only apply to authenticated patients
    }

    const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
    let inactivityTimer;

    const resetTimer = () => {
      // Clear existing timer
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }

      // Set new timer for auto-logout
      inactivityTimer = setTimeout(() => {
        console.log('Auto-logout due to inactivity');
        toast.error('You have been logged out due to inactivity');
        logout();
      }, INACTIVITY_TIMEOUT);
    };

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Set initial timer
    resetTimer();

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    // Cleanup on unmount or when user changes
    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      activityEvents.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
    };
  }, [isAuthenticated, user, logout]);

  const updateUser = (updatedUserData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUserData
    }));
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    checkAuthStatus,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
